import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Check,
  Copy,
  CreditCard,
  Loader2,
  MessageCircle,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  isAnnualOfferedToTenant,
  tenantPlanPrice,
} from "@/lib/tenant-plan-pricing";
import {
  getTenantPlansForTenant,
  type TenantPlanOption,
} from "@/services/authService";
import {
  cancelPlanCheckoutForTenant,
  createBankTransferCheckoutForTenant,
  createBankTransferIntentForTenant,
  createPlanCheckoutForTenant,
  getPendingPlanCheckoutForTenant,
  getPlanCheckoutForTenant,
} from "@/services/planPaymentsService";
import {
  BankTransferPanel,
  grossWithIva,
} from "@/components/features/accountant/BankTransferPanel";

const UYU_FORMATTER = new Intl.NumberFormat("es-UY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Minimal tenant shape the dialog needs — id for the checkout call, name/rut
 * for the WhatsApp/copy messaging. Both the accountant home-page TenantSummary
 * and the client-detail AccountantTenantDetailHeader satisfy it structurally,
 * so the same dialog drives the "migrar a plan pago" (free→paid) flow and the
 * "generar link de pago" reactivation flow.
 */
export interface GeneratePaymentLinkTenant {
  id: string;
  name: string | null;
  rut: string | null;
}

/**
 * Shared plan-checkout link generator for accountants. Picks a plan + interval,
 * mints a `fiscalo.link` short URL via createPlanCheckoutForTenant, and lets the
 * accountant copy/WhatsApp it to the client. Recovers an in-flight pending link
 * so a lost link doesn't force a regenerate. Extracted from accountant-home-page
 * so both the home-page 3-dots action and the client-detail page reuse it (spec
 * c005e72f).
 *
 * `forcePickerOnOpen` seeds the picker directly (skipping the recover-pending
 * view) — the reactivation entry from the client-detail page passes true so the
 * accountant always lands on the plan chooser to mint a fresh link.
 */
export function GeneratePaymentLinkDialog({
  tenant,
  onClose,
  forcePickerOnOpen = false,
}: {
  tenant: GeneratePaymentLinkTenant | null;
  onClose: () => void;
  forcePickerOnOpen?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const open = tenant != null;
  const queryClient = useQueryClient();

  // Client-aware: this specific tenant's eligible plans at the price THEY will
  // actually pay (80 UI discount from their own declaration), not the
  // accountant's context.
  const { data: plansData } = useQuery({
    queryKey: ["tenant-plans-for-tenant", tenant?.id],
    queryFn: () => getTenantPlansForTenant(tenant!.id),
    enabled: open && tenant != null,
  });
  const plans = plansData?.plans ?? [];
  const eligiblePlanIds = plansData?.eligiblePlanIds ?? [];
  // Show only the plans this client qualifies for. Drop the `literalE` tier
  // card: it is economically Tala, and for an Art. 5 (Literal E) client Tala
  // already shows the discounted $0 — a separate literalE card would duplicate
  // it. The 80 UI discount is applied server-side regardless of the tier picked.
  const paidPlans = plans.filter(
    (p) =>
      p.id !== "free" && p.id !== "literalE" && eligiblePlanIds.includes(p.id),
  );
  const annualOffered = isAnnualOfferedToTenant(paidPlans);

  // Pull any in-flight pending link for this tenant so the "client
  // lost the link" case doesn't require regenerating (and superseding
  // the original). One pending checkout per tenant — generating a new
  // one cancels the prior server-side.
  const { data: pendingCheckout, isFetching: loadingPending } = useQuery({
    queryKey: ["accountant-pending-checkout", tenant?.id],
    queryFn: () => getPendingPlanCheckoutForTenant(tenant!.id),
    enabled: open && tenant != null,
  });

  const [planId, setPlanId] = useState<string | null>(null);
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fresh, setFresh] = useState<{
    checkoutId: string;
    shortUrl: string;
  } | null>(null);
  // false = recover-pending view, true = picker view (used when the user
  // explicitly chooses "generate a different link" while one is pending,
  // or when opened from the reactivation entry via forcePickerOnOpen).
  const [forcePicker, setForcePicker] = useState(false);
  // Annual bank-transfer sub-flow (mirrors the accountant onboarding page):
  // upload the wire receipt → the plan activates immediately, no dLocal.
  const [bankTransferOpen, setBankTransferOpen] = useState(false);
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  const [transferActivated, setTransferActivated] = useState(false);
  /**
   * The checkout the client will wire against, opened when the panel is
   * expanded so the accountant has a reference to pass on. Per-tenant, like
   * the submit below — an intent against the session's active tenant would
   * mint it on whichever client happens to be loaded.
   */
  const [transferCheckoutId, setTransferCheckoutId] = useState<string | null>(
    null,
  );

  // Reset every time the dialog opens for a different tenant so the
  // previous tenant's generated link doesn't leak across.
  useEffect(() => {
    if (!open) return;
    setPlanId(null);
    setInterval("monthly");
    setFresh(null);
    setForcePicker(forcePickerOnOpen);
    setGenerating(false);
    setDeleting(false);
    setBankTransferOpen(false);
    setSubmittingTransfer(false);
    setTransferActivated(false);
    setTransferCheckoutId(null);
  }, [open, tenant?.id, forcePickerOnOpen]);


  // Default-select the cheapest paid plan once plans land.
  useEffect(() => {
    if (planId == null && paidPlans.length > 0) {
      setPlanId(paidPlans[0]!.id);
    }
  }, [planId, paidPlans]);

  const selectedPlan: TenantPlanOption | null =
    paidPlans.find((p) => p.id === planId) ?? null;

  /**
   * Open (or adopt) the checkout when the panel is expanded, and again if the
   * plan or cycle changes while it is open. The backend reuses a matching
   * pending row, so this neither churns rows nor cancels a payment link the
   * accountant already generated for the same plan.
   */
  useEffect(() => {
    if (!open || !bankTransferOpen || !tenant || !selectedPlan) return;
    let cancelled = false;
    void createBankTransferIntentForTenant(tenant.id, {
      planId: selectedPlan.id,
      billingInterval: interval,
      returnContext: "shared_link",
    })
      .then((res) => {
        if (!cancelled) setTransferCheckoutId(res.checkoutId);
      })
      .catch(() => {
        if (!cancelled) setTransferCheckoutId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bankTransferOpen, tenant, selectedPlan, interval]);

  const handleGenerate = async (): Promise<void> => {
    if (!tenant || !selectedPlan || generating) return;
    setGenerating(true);
    try {
      // Generating a link cancels every pending row for this tenant — the
      // transfer intent among them — so stop showing its reference.
      setTransferCheckoutId(null);
      const res = await createPlanCheckoutForTenant(tenant.id, {
        planId: selectedPlan.id,
        billingInterval: interval,
        returnContext: "shared_link",
      });
      if (!res.shortUrl) {
        toast.error(
          t(
            "accountantUpgrade.noUrlError",
            "No pudimos generar el enlace. Probá de nuevo.",
          ),
        );
        return;
      }
      setFresh({ checkoutId: res.checkoutId, shortUrl: res.shortUrl });
      setForcePicker(false);
      await queryClient.invalidateQueries({
        queryKey: ["accountant-pending-checkout", tenant.id],
      });
      toast.success(
        t("accountantUpgrade.linkGenerated", "Enlace listo para compartir."),
      );
    } catch {
      toast.error(
        t("accountantUpgrade.generateError", "No pudimos generar el enlace."),
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleBankTransfer = async (
    receipt: File,
    shownCheckoutId: string | null,
  ): Promise<void> => {
    if (!tenant || !selectedPlan || submittingTransfer) {
      return;
    }
    setSubmittingTransfer(true);
    try {
      const result = await createBankTransferCheckoutForTenant(tenant.id, {
        planId: selectedPlan.id,
        billingInterval: interval,
        receipt,
        // The row the client was given a reference for.
        settleCheckoutId: shownCheckoutId ?? undefined,
      });
      setBankTransferOpen(false);
      // A monthly transfer whose receipt did not verify is HELD: the payment
      // is recorded but the plan is NOT granted until a human approves it.
      // Saying "quedó activo" there would be a lie the tenant discovers when
      // they try to emit.
      const activated = result?.activated !== false;
      setTransferActivated(activated);
      // The plan is live immediately (no dLocal) — flush the caches so the
      // cartera card + client-detail reflect the active plan.
      await queryClient.invalidateQueries({ queryKey: ["accountant"] });
      await queryClient.invalidateQueries({
        queryKey: ["accountant-pending-checkout", tenant.id],
      });
      toast.success(
        activated
          ? t(
              "accountantUpgrade.bankTransferActivated",
              "¡Pago registrado! El plan quedó activo.",
            )
          : t(
              "accountantUpgrade.bankTransferHeld",
              "Recibimos el comprobante. Lo estamos revisando y te avisamos apenas quede activo.",
            ),
      );
    } catch {
      toast.error(
        t(
          "accountantUpgrade.bankTransferError",
          "No pudimos registrar el pago. Probá de nuevo.",
        ),
      );
    } finally {
      setSubmittingTransfer(false);
    }
  };

  const handleCopy = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("accountantUpgrade.copied", "Enlace copiado."));
    } catch {
      toast.error(
        t("accountantUpgrade.copyError", "No pudimos copiar el enlace."),
      );
    }
  };

  const handleWhatsApp = (url: string): void => {
    if (!tenant) return;
    const name =
      tenant.name?.trim() ||
      tenant.rut ||
      t("accountant.home.unnamed", "Empresa sin nombre");
    const message = t("accountantUpgrade.whatsappMessage", {
      name,
      url,
      defaultValue:
        "Hola — para activar tu plan en Fiscalo para {{name}}, completá el pago acá: {{url}}",
    });
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  // Source-of-truth ranking for what to render. A freshly-generated
  // link wins; otherwise we surface the server's pending row; if
  // neither, we draw the picker.
  const displayedUrl =
    fresh?.shortUrl ??
    (forcePicker ? null : pendingCheckout?.shortUrl ?? null);
  const displayedCheckoutId =
    fresh?.checkoutId ??
    (forcePicker ? null : pendingCheckout?.checkoutId ?? null);
  const displayedPlanId =
    fresh != null
      ? selectedPlan?.id ?? null
      : pendingCheckout?.planId ?? null;
  const displayedInterval =
    fresh != null
      ? interval
      : (pendingCheckout?.billingInterval as "monthly" | "annual" | undefined) ??
        null;
  const displayedPlan =
    displayedPlanId != null
      ? plans.find((p) => p.id === displayedPlanId) ?? null
      : null;

  const handleDelete = async (): Promise<void> => {
    if (!tenant || !displayedCheckoutId || deleting) return;
    setDeleting(true);
    try {
      await cancelPlanCheckoutForTenant(tenant.id, displayedCheckoutId);
      await queryClient.invalidateQueries({
        queryKey: ["accountant-pending-checkout", tenant.id],
      });
      setFresh(null);
      toast.success(t("accountantUpgrade.deleted", "Enlace eliminado."));
      onClose();
    } catch {
      toast.error(
        t("accountantUpgrade.deleteError", "No pudimos eliminar el enlace."),
      );
    } finally {
      setDeleting(false);
    }
  };

  // Poll the specific displayed checkout — when it flips to 'paid'
  // (dLocal webhook lands), surface the success view and refresh
  // the tenant card so the badge moves off "Tala · Gratis" without
  // forcing a manual reload. 15s matches the onboarding page; the
  // client needs minutes to actually fill in card details, so a
  // faster cadence just burns DB hits for no UX gain.
  const { data: checkoutStatus } = useQuery({
    queryKey: ["accountant-checkout-status", tenant?.id, displayedCheckoutId],
    queryFn: () => getPlanCheckoutForTenant(tenant!.id, displayedCheckoutId!),
    enabled: open && tenant != null && displayedCheckoutId != null,
    refetchInterval: (q) => (q.state.data?.status === "paid" ? false : 15_000),
  });
  const paid = checkoutStatus?.status === "paid";

  // When the webhook lands, flush the portfolio caches so every
  // mounted card (and the pulse) reflects the new plan immediately.
  useEffect(() => {
    if (!paid || !tenant) return;
    void queryClient.invalidateQueries({ queryKey: ["accountant"] });
    void queryClient.invalidateQueries({
      queryKey: ["accountant-pending-checkout", tenant.id],
    });
  }, [paid, tenant, queryClient]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            {paid
              ? t("accountantUpgrade.paidTitle", "¡Pago confirmado!")
              : t("accountantUpgrade.title", "Migrar a plan pago")}
          </DialogTitle>
          <DialogDescription>
            {paid
              ? t("accountantUpgrade.paidDescription", {
                  name:
                    tenant?.name?.trim() ||
                    tenant?.rut ||
                    t("accountant.home.unnamed", "Empresa sin nombre"),
                  defaultValue:
                    "{{name}} ya tiene el plan activo. Te enviamos un correo con la confirmación.",
                })
              : t("accountantUpgrade.description", {
                  name:
                    tenant?.name?.trim() ||
                    tenant?.rut ||
                    t("accountant.home.unnamed", "Empresa sin nombre"),
                  defaultValue:
                    "Elegí un plan para {{name}} y compartí el enlace de pago con el cliente.",
                })}
          </DialogDescription>
        </DialogHeader>

        {paid || transferActivated ? (
          <PaidView
            plan={transferActivated ? selectedPlan : displayedPlan}
            interval={transferActivated ? "annual" : displayedInterval ?? "monthly"}
            onClose={onClose}
          />
        ) : loadingPending && pendingCheckout === undefined ? (
          <div className="flex items-center justify-center py-6">
            <Loader2
              className="h-5 w-5 animate-spin"
              style={{ color: "var(--fx-ink-500)" }}
              aria-hidden
            />
          </div>
        ) : displayedUrl ? (
          <ShareLinkView
            url={displayedUrl}
            isFresh={fresh != null}
            plan={displayedPlan}
            interval={displayedInterval ?? "monthly"}
            onCopy={() => void handleCopy(displayedUrl)}
            onWhatsApp={() => handleWhatsApp(displayedUrl)}
            onReplace={() => {
              setFresh(null);
              setForcePicker(true);
            }}
            onDelete={() => void handleDelete()}
            deleting={deleting}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <PickerView
              plans={paidPlans}
              planId={planId}
              onPlanId={setPlanId}
              interval={interval}
              onInterval={setInterval}
              selectedPlan={selectedPlan}
              generating={generating}
              onGenerate={handleGenerate}
              hadPending={pendingCheckout != null}
              onCancelReplace={() => setForcePicker(false)}
              annualOffered={annualOffered}
            />
            {/* Pay by bank transfer: upload the receipt and the plan
                activates. Monthly verifies the receipt FIRST and can hold the
                grant pending review (spec a9b1ccad); annual activates
                immediately and verifies after. Both cycles are offered — the
                old "elegí el ciclo anual" hint is gone because monthly is no
                longer refused by the backend. */}
            {selectedPlan ? (
              <BankTransferPanel
                open={bankTransferOpen}
                onToggle={() => setBankTransferOpen((o) => !o)}
                onSubmit={handleBankTransfer}
                submitting={submittingTransfer}
                amountGrossPesos={grossWithIva(
                  tenantPlanPrice(selectedPlan, interval),
                )}
                checkoutId={transferCheckoutId}
                billingInterval={interval}
              />
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareLinkView({
  url,
  isFresh,
  plan,
  interval,
  onCopy,
  onWhatsApp,
  onReplace,
  onDelete,
  deleting,
}: {
  url: string;
  isFresh: boolean;
  plan: TenantPlanOption | null;
  interval: "monthly" | "annual";
  onCopy: () => void;
  onWhatsApp: () => void;
  onReplace: () => void;
  onDelete: () => void;
  deleting: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const price = plan ? tenantPlanPrice(plan, interval) : 0;
  const cadenceLabel =
    interval === "annual"
      ? t("settings.plans.annual", "Anual")
      : t("settings.plans.monthly", "Mensual");
  return (
    <div className="flex flex-col gap-4">
      {plan ? (
        <div
          className="rounded-md border px-3 py-2 text-xs flex items-center justify-between"
          style={{
            borderColor: "var(--fx-s-hairline)",
            background: "var(--fx-s-overlay)",
            color: "var(--fx-ink-700)",
          }}
        >
          <span>
            {t(plan.nameKey, plan.id.toUpperCase())} · {cadenceLabel}
          </span>
          <span style={{ color: "var(--fx-s-text)", fontWeight: 700 }}>
            ${UYU_FORMATTER.format(price)} UYU{" "}
            <span style={{ color: "var(--fx-ink-500)", fontWeight: 500 }}>
              {t("settings.plans.plusIva", "+ IVA")}
            </span>
          </span>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <input
          type="text"
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 min-w-0 rounded-[var(--fx-r-pill)] border px-3 py-2 text-sm font-mono"
          style={{
            borderColor: "var(--fx-ink-200)",
            background: "var(--fx-s-overlay)",
            color: "var(--fx-s-text)",
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onCopy}
          aria-label={t("accountantUpgrade.copy", "Copiar enlace")}
        >
          <Copy />
        </Button>
      </div>

      <p
        className="text-xs leading-relaxed"
        style={{ color: "var(--fx-ink-500)" }}
      >
        {isFresh
          ? t(
              "accountantUpgrade.freshLinkNote",
              "El enlace queda asociado a esta empresa. Quien lo pague activa esta suscripción específicamente.",
            )
          : t(
              "accountantUpgrade.pendingLinkNote",
              "Hay un enlace en curso para esta empresa. Compartilo de nuevo, o generá uno nuevo (el anterior queda inválido).",
            )}
      </p>

      <DialogFooter className="gap-2 sm:gap-2">
        <Button
          type="button"
          variant="destructive"
          onClick={onDelete}
          disabled={deleting}
          className="sm:mr-auto"
        >
          {deleting ? <Loader2 className="animate-spin" /> : <Trash2 />}
          {t("accountantUpgrade.delete", "Eliminar enlace")}
        </Button>
        <Button type="button" variant="ghost" onClick={onReplace}>
          {t("accountantUpgrade.generateAnother", "Generar otro")}
        </Button>
        <Button type="button" onClick={onWhatsApp}>
          <MessageCircle />
          {t("accountantUpgrade.whatsapp", "Enviar por WhatsApp")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function PaidView({
  plan,
  interval,
  onClose,
}: {
  plan: TenantPlanOption | null;
  interval: "monthly" | "annual";
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const price = plan ? tenantPlanPrice(plan, interval) : 0;
  const cadenceLabel =
    interval === "annual"
      ? t("settings.plans.annual", "Anual")
      : t("settings.plans.monthly", "Mensual");
  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-md border px-4 py-4 flex items-center gap-3"
        style={{
          borderColor: "var(--fx-accent-300)",
          background: "var(--fx-accent-100)",
          color: "var(--fx-s-text)",
        }}
      >
        <span
          className="grid place-items-center rounded-full"
          style={{
            width: 36,
            height: 36,
            background: "var(--fx-accent-500)",
            color: "white",
          }}
          aria-hidden
        >
          <Check style={{ width: 18, height: 18 }} />
        </span>
        <div className="min-w-0 flex-1">
          {plan ? (
            <div className="text-sm font-semibold">
              {t(plan.nameKey, plan.id.toUpperCase())} · {cadenceLabel} ·{" "}
              ${UYU_FORMATTER.format(price)} UYU{" "}
              <span style={{ color: "var(--fx-ink-500)", fontWeight: 500 }}>
                {t("settings.plans.plusIva", "+ IVA")}
              </span>
            </div>
          ) : null}
          <div className="text-xs mt-0.5" style={{ color: "var(--fx-ink-700)" }}>
            {t(
              "accountantUpgrade.paidNote",
              "La suscripción quedó activa. La empresa pasa al plan pago.",
            )}
          </div>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:gap-2">
        <Button type="button" onClick={onClose}>
          {t("accountantUpgrade.paidClose", "Listo")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function PickerView({
  plans,
  planId,
  onPlanId,
  interval,
  onInterval,
  selectedPlan,
  generating,
  onGenerate,
  hadPending,
  onCancelReplace,
  annualOffered,
}: {
  plans: TenantPlanOption[];
  planId: string | null;
  onPlanId: (id: string) => void;
  interval: "monthly" | "annual";
  onInterval: (next: "monthly" | "annual") => void;
  selectedPlan: TenantPlanOption | null;
  generating: boolean;
  onGenerate: () => void | Promise<void>;
  hadPending: boolean;
  onCancelReplace: () => void;
  /** Annual billing is withheld for benefit (80 UI) tenants — hide the toggle
   *  rather than offer a cycle the checkout rejects. */
  annualOffered: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const price = selectedPlan ? tenantPlanPrice(selectedPlan, interval) : 0;
  const intervals: ReadonlyArray<"monthly" | "annual"> = annualOffered
    ? (["monthly", "annual"] as const)
    : (["monthly"] as const);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2">
        {plans.map((p) => {
          const isSelected = planId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPlanId(p.id)}
              className={cn(
                "rounded-md border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-2",
              )}
              style={{
                borderColor: isSelected
                  ? "var(--fx-accent-500)"
                  : "var(--fx-ink-200)",
                background: isSelected ? "var(--fx-accent-100)" : "white",
              }}
              aria-pressed={isSelected}
            >
              <div
                className="text-sm font-semibold"
                style={{ color: "var(--fx-s-text)" }}
              >
                {t(p.nameKey, p.id.toUpperCase())}
              </div>
              <div
                className="text-xs mt-0.5"
                style={{ color: "var(--fx-ink-500)" }}
              >
                {p.maxCfesPerMonth == null
                  ? t("accountantUpgrade.unlimitedCfe", "CFE ilimitados / mes")
                  : t("accountantUpgrade.maxCfe", {
                      count: p.maxCfesPerMonth,
                      defaultValue: "Hasta {{count}} CFE / mes",
                    })}
              </div>
            </button>
          );
        })}
      </div>

      <div
        className={cn(
          "grid gap-2",
          intervals.length > 1 ? "grid-cols-2" : "grid-cols-1",
        )}
      >
        {intervals.map((opt) => {
          const isSelected = interval === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onInterval(opt)}
              className="rounded-md border px-3 py-2 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-2"
              style={{
                borderColor: isSelected
                  ? "var(--fx-accent-500)"
                  : "var(--fx-ink-200)",
                background: isSelected ? "var(--fx-accent-100)" : "white",
                color: "var(--fx-s-text)",
                fontWeight: 600,
              }}
              aria-pressed={isSelected}
            >
              {opt === "monthly"
                ? t("settings.plans.monthly", "Mensual")
                : t("settings.plans.annual", "Anual")}
            </button>
          );
        })}
      </div>

      {selectedPlan ? (
        <div
          className="rounded-md border px-3 py-2 text-xs flex items-center justify-between"
          style={{
            borderColor: "var(--fx-s-hairline)",
            background: "var(--fx-s-overlay)",
            color: "var(--fx-ink-700)",
          }}
        >
          <span>
            {t(selectedPlan.nameKey, selectedPlan.id.toUpperCase())} ·{" "}
            {interval === "monthly"
              ? t("settings.plans.monthly", "Mensual")
              : t("settings.plans.annual", "Anual")}
          </span>
          <span style={{ color: "var(--fx-s-text)", fontWeight: 700 }}>
            ${UYU_FORMATTER.format(price)} UYU{" "}
            <span style={{ color: "var(--fx-ink-500)", fontWeight: 500 }}>
              {t("settings.plans.plusIva", "+ IVA")}
            </span>
          </span>
        </div>
      ) : null}

      {hadPending ? (
        <p
          className="text-xs leading-relaxed"
          style={{ color: "var(--fx-ink-500)" }}
        >
          {t(
            "accountantUpgrade.replaceWarning",
            "Si generás un enlace nuevo, el anterior queda inválido.",
          )}
        </p>
      ) : null}

      <DialogFooter className="gap-2 sm:gap-2">
        {hadPending ? (
          <Button type="button" variant="ghost" onClick={onCancelReplace}>
            {t("pages.common.cancel", "Cancelar")}
          </Button>
        ) : null}
        <Button
          type="button"
          disabled={!selectedPlan || generating}
          onClick={() => void onGenerate()}
        >
          {generating ? <Loader2 className="animate-spin" /> : <CreditCard />}
          {t("accountantUpgrade.generate", "Generar enlace de pago")}
        </Button>
      </DialogFooter>
    </div>
  );
}
